import { useCallback, useEffect, useRef, useState } from "react";
import { StreamingHelper } from "@scrile/streaming-client/build";
import { onNetworkRecovery } from "../lib/networkRecovery";

// Doubled from 5 on 2026-06-15: at 5 attempts the call gave up after only
// ~31 s of backoff, so a single slow-network burst (mobile handoff, VPN
// switch with cold ICE) routinely flipped the conference to "failed" with
// no recovery path. 10 attempts spread the giveup window to ~3 minutes,
// long enough for any realistic transient and still bounded so a truly
// dead transport doesn't loop forever.
const MAX_RETRIES = 10;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
// After the retry budget is exhausted we drop into a quiet "failed" state
// and try one more full rejoin after this delay. This is a separate path
// from `onReconnect` (which rebuilds streams in the EXISTING livechat
// session). `onRejoin`, if provided, can re-create the livechat join
// itself — closing whatever zombie session sits on the server and
// starting fresh. Empirically a 60 s wait gives flaky cell networks the
// best chance of being usable again.
const AUTO_REJOIN_DELAY_MS = 60000;

export type ConnectionStatus = "connected" | "reconnecting" | "disconnected" | "failed";
/** Reason for a reconnect attempt — recorded so post-mortem diagnostics
 *  can tell whether a flap was network-side, ICE-side, or signalled. */
type ReconnectReason =
  | "ice-failed"
  | "ice-disconnected"
  | "ice-close"
  | "browser-online"
  | "network-recover"
  | "retry-failure"
  | "auto-rejoin";

interface UseConnectionReconnectOptions {
  webRTC: StreamingHelper | null;
  onReconnect: () => Promise<void>;
  /** Optional path used after `MAX_RETRIES` reconnect attempts have all
   *  failed. Typically tears down and recreates the livechat session
   *  itself (`LivechatsProvider.watch` / `broadcast`) — something
   *  `onReconnect` cannot do because it operates inside the existing
   *  session. If omitted, the hook stays in "failed" state and waits
   *  for a manual rejoin. */
  onRejoin?: () => Promise<void>;
  enabled?: boolean;
}

export default function useConnectionReconnect({
  webRTC,
  onReconnect,
  onRejoin,
  enabled = true,
}: UseConnectionReconnectOptions) {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("connected");
  // Seconds until the auto-rejoin attempt fires. Surfaces to the UI as a
  // visible countdown so the user knows the app is going to recover on
  // its own (vs. them having to click "Rejoin").
  const [rejoinInSeconds, setRejoinInSeconds] = useState<number | null>(null);
  const retryCount = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rejoinTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rejoinTickTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnecting = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const onRejoinRef = useRef(onRejoin);
  onRejoinRef.current = onRejoin;
  // Last known ICE state. Lets the deferred "disconnected → reconnect"
  // timer abort if the link self-healed during the grace window, which
  // would otherwise trigger a useless full rebuild and flash media for
  // every other participant.
  const lastStateRef = useRef<string>("connected");

  const clearRetryTimer = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  }, []);

  const clearRejoinTimers = useCallback(() => {
    if (rejoinTimer.current) {
      clearTimeout(rejoinTimer.current);
      rejoinTimer.current = null;
    }
    if (rejoinTickTimer.current) {
      clearInterval(rejoinTickTimer.current);
      rejoinTickTimer.current = null;
    }
    setRejoinInSeconds(null);
  }, []);

  const scheduleAutoRejoin = useCallback(() => {
    if (!onRejoinRef.current) return;
    if (rejoinTimer.current) return; // already scheduled
    let remaining = Math.floor(AUTO_REJOIN_DELAY_MS / 1000);
    setRejoinInSeconds(remaining);
    // eslint-disable-next-line no-console
    console.info(`[reconnect] Scheduling auto-rejoin in ${remaining}s`);
    rejoinTickTimer.current = setInterval(() => {
      remaining -= 1;
      setRejoinInSeconds(remaining > 0 ? remaining : 0);
      if (remaining <= 0 && rejoinTickTimer.current) {
        clearInterval(rejoinTickTimer.current);
        rejoinTickTimer.current = null;
      }
    }, 1000);
    rejoinTimer.current = setTimeout(async () => {
      rejoinTimer.current = null;
      if (!enabledRef.current) return;
      // eslint-disable-next-line no-console
      console.warn("[reconnect] auto-rejoin firing");
      try {
        await onRejoinRef.current!();
        retryCount.current = 0;
        reconnecting.current = false;
        setConnectionStatus("connected");
        setRejoinInSeconds(null);
        // eslint-disable-next-line no-console
        console.warn("[reconnect] auto-rejoin succeeded");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[reconnect] auto-rejoin failed, staying in failed state for manual retry:", e);
        setRejoinInSeconds(null);
      }
    }, AUTO_REJOIN_DELAY_MS);
  }, []);

  const attemptReconnect = useCallback(async (reason: ReconnectReason = "retry-failure") => {
    if (reconnecting.current || !enabledRef.current) return;
    if (retryCount.current >= MAX_RETRIES) {
      // eslint-disable-next-line no-console
      console.warn(`[reconnect] Max retries (${MAX_RETRIES}) reached, giving up — entering failed state with auto-rejoin in ${Math.floor(AUTO_REJOIN_DELAY_MS / 1000)}s`);
      setConnectionStatus("failed");
      scheduleAutoRejoin();
      return;
    }

    reconnecting.current = true;
    retryCount.current += 1;
    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount.current - 1), MAX_DELAY_MS);
    // eslint-disable-next-line no-console
    console.warn(`[reconnect] Attempt ${retryCount.current}/${MAX_RETRIES} in ${delay}ms (reason=${reason})`);
    setConnectionStatus("reconnecting");

    retryTimer.current = setTimeout(async () => {
      try {
        await onReconnect();
        retryCount.current = 0;
        reconnecting.current = false;
        setConnectionStatus("connected");
        clearRejoinTimers();
        // eslint-disable-next-line no-console
        console.warn("[reconnect] Successfully reconnected");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[reconnect] Reconnect attempt failed:", e);
        reconnecting.current = false;
        attemptReconnect("retry-failure");
      }
    }, delay);
  }, [onReconnect, scheduleAutoRejoin, clearRejoinTimers]);

  useEffect(() => {
    if (!enabled || !webRTC) return;

    const handleStateChange = (state: string) => {
      // eslint-disable-next-line no-console
      console.info("[reconnect] connectionstatechange:", state);
      lastStateRef.current = state;

      if (state === "connected") {
        retryCount.current = 0;
        reconnecting.current = false;
        clearRetryTimer();
        clearRejoinTimers();
        setConnectionStatus("connected");
      } else if (state === "disconnected") {
        setConnectionStatus("disconnected");
        // Brief disconnect — wait a moment before attempting reconnect
        // (ICE can self-recover from brief disconnects)
        if (!reconnecting.current) {
          clearRetryTimer();
          retryTimer.current = setTimeout(() => {
            // If ICE healed itself during the grace window, skip the
            // rebuild — onReconnect tears down working transports.
            if (lastStateRef.current === "connected") return;
            attemptReconnect("ice-disconnected");
          }, 3000);
        }
      } else if (state === "failed") {
        clearRetryTimer();
        attemptReconnect("ice-failed");
      }
    };

    const handleClose = () => {
      // eslint-disable-next-line no-console
      console.info("[reconnect] Connection closed, attempting reconnect...");
      attemptReconnect("ice-close");
    };

    webRTC.on("connectionstatechange", handleStateChange);
    webRTC.on("close", handleClose);

    return () => {
      webRTC.off("connectionstatechange", handleStateChange);
      webRTC.off("close", handleClose);
      clearRetryTimer();
      clearRejoinTimers();
    };
  }, [webRTC, enabled, attemptReconnect, clearRetryTimer, clearRejoinTimers]);

  // Also listen for browser online/offline events + host-app network
  // recovery (mobile resume, Capacitor network reconnect, etc.).
  useEffect(() => {
    if (!enabled) return;

    const handleOnline = () => {
      // eslint-disable-next-line no-console
      console.info("[reconnect] Browser back online, attempting reconnect...");
      // Reset the backoff counter and clear any in-flight backoff timer so
      // the new attempt isn't suppressed by `reconnecting.current` or
      // delayed by the leftover exponential timer.
      clearRetryTimer();
      reconnecting.current = false;
      retryCount.current = 0;
      attemptReconnect("browser-online");
    };

    const handleOffline = () => {
      setConnectionStatus("disconnected");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    const unsubRecover = onNetworkRecovery(() => {
      // Cancel any in-flight backoff so the recovery signal doesn't get
      // swallowed by `attemptReconnect`'s `reconnecting.current` guard.
      clearRetryTimer();
      reconnecting.current = false;
      retryCount.current = 0;
      attemptReconnect("network-recover");
    });

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      unsubRecover();
    };
  }, [enabled, attemptReconnect, clearRetryTimer]);

  const resetRetries = useCallback(() => {
    retryCount.current = 0;
    reconnecting.current = false;
    clearRetryTimer();
    clearRejoinTimers();
    setConnectionStatus("connected");
  }, [clearRetryTimer, clearRejoinTimers]);

  /** Manual "try now" handler — surfaces to the UI when the user can't
   *  wait for the auto-rejoin countdown. Cancels the pending timer and
   *  invokes `onRejoin` synchronously. */
  const rejoinNow = useCallback(async () => {
    if (!onRejoinRef.current) return;
    clearRejoinTimers();
    setConnectionStatus("reconnecting");
    try {
      await onRejoinRef.current();
      retryCount.current = 0;
      reconnecting.current = false;
      setConnectionStatus("connected");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[reconnect] manual rejoin failed:", e);
      setConnectionStatus("failed");
    }
  }, [clearRejoinTimers]);

  return {
    connectionStatus,
    resetRetries,
    rejoinInSeconds,
    rejoinNow,
  };
}
